import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Select,
  Text,
  Heading,
  HStack,
  Alert,
  AlertIcon,
  AlertDescription,
  Spinner,
} from '@chakra-ui/react';
import { FaGlobe, FaCheckCircle } from 'react-icons/fa';

const LanguageSelector = () => {
  const [language, setLanguage] = useState('en');
  const [isLoading, setIsLoading] = useState(false);
  const [translatorReady, setTranslatorReady] = useState(false);

  const languages = [
    { code: 'en', name: 'English', native: 'English' },
    { code: 'es', name: 'Spanish', native: 'Español' },
    { code: 'fr', name: 'French', native: 'Français' },
    { code: 'de', name: 'German', native: 'Deutsch' },
    { code: 'it', name: 'Italian', native: 'Italiano' },
    { code: 'pt', name: 'Portuguese', native: 'Português' },
    { code: 'ru', name: 'Russian', native: 'Русский' },
    { code: 'zh-CN', name: 'Chinese (Simplified)', native: '简体中文' },
    { code: 'zh-TW', name: 'Chinese (Traditional)', native: '繁體中文' },
    { code: 'ja', name: 'Japanese', native: '日本語' },
    { code: 'ko', name: 'Korean', native: '한국어' },
    { code: 'ar', name: 'Arabic', native: 'العربية' },
    { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
    { code: 'vi', name: 'Vietnamese', native: 'Tiếng Việt' },
    { code: 'th', name: 'Thai', native: 'ไทย' },
  ];

  useEffect(() => {
    // Load saved language preference
    const saved = localStorage.getItem('selectedLanguage');
    if (saved) {
      setLanguage(saved);
    }
    
    // Initialize Google Translate once
    initializeGoogleTranslate();
  }, []);

  const initializeGoogleTranslate = () => {
    try {
      // Check if already loaded
      if (window.google?.translate?.TranslateElement) {
        initTranslateElement();
        return;
      }

      // Prevent duplicate initialization
      if (window.googleTranslateInit) {
        return;
      }

      window.googleTranslateInit = true;

      // Define the callback function BEFORE loading script
      window.googleTranslateElementInit = function() {
        try {
          initTranslateElement();
        } catch (error) {
          console.error('Google Translate initialization error:', error);
          setTranslatorReady(false);
        }
      };

      // Load Google Translate script
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      script.onerror = () => {
        console.error('Failed to load Google Translate script');
        setTranslatorReady(false);
      };
      
      document.body.appendChild(script);
    } catch (error) {
      console.error('Error initializing Google Translate:', error);
    }
  };

  const initTranslateElement = () => {
    try {
      if (!document.getElementById('google_translate_element')) {
        console.error('Google Translate container not found');
        return;
      }

      new window.google.translate.TranslateElement({
        pageLanguage: 'en',
        includedLanguages: 'es,fr,de,it,pt,ru,zh-CN,zh-TW,ja,ko,ar,hi,vi,th',
        layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        autoDisplay: false,
      }, 'google_translate_element');

      setTranslatorReady(true);
    } catch (error) {
      console.error('Error creating translate element:', error);
      setTranslatorReady(false);
    }
  };

  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    
    try {
      setIsLoading(true);
      setLanguage(newLang);
      localStorage.setItem('selectedLanguage', newLang);
      
      // Set HTML lang attribute
      document.documentElement.lang = newLang;

      // Use Google Translate's cookie-based method (safer than hash)
      if (newLang !== 'en') {
        // Set the translation cookie
        const cookieName = 'googtrans';
        const cookieValue = `/en/${newLang}`;
        const domain = window.location.hostname;
        document.cookie = `${cookieName}=${cookieValue}; path=/; domain=${domain}`;
        
        // Trigger Google Translate
        const translateFrame = document.querySelector('.goog-te-menu-frame');
        if (translateFrame) {
          // Use Google's translate function if available
          if (window.google?.translate?.TranslateElement) {
            setTimeout(() => {
              window.location.reload();
            }, 500);
          }
        } else {
          // Fallback: reload page with cookie set
          setTimeout(() => {
            window.location.reload();
          }, 300);
        }
      } else {
        // Reset translation
        document.cookie = 'googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        setTimeout(() => {
          window.location.reload();
        }, 300);
      }
    } catch (error) {
      console.error('Language change error:', error);
      setIsLoading(false);
      alert('Sorry, there was an error changing the language. Please try again.');
    }
  };

  return (
    <Box>
      <HStack mb={4}>
        <FaGlobe color="#2196f3" size={20} />
        <Heading size="sm" color="brand.700">
          Language Translation
        </Heading>
      </HStack>

      <VStack spacing={4} align="stretch">
        
        <Box>
          <Text fontSize="sm" fontWeight="600" mb={2}>
            Select Language
          </Text>
          <Select
            value={language}
            onChange={handleLanguageChange}
            size="md"
            isDisabled={isLoading}
          >
            {languages.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.native} ({lang.name})
              </option>
            ))}
          </Select>

          {isLoading && (
            <HStack mt={2} spacing={2}>
              <Spinner size="sm" color="brand.500" />
              <Text fontSize="xs" color="gray.600">
                Changing language...
              </Text>
            </HStack>
          )}

          {translatorReady && !isLoading && (
            <HStack mt={2} spacing={2} color="green.600">
              <FaCheckCircle size={12} />
              <Text fontSize="xs">
                Translator ready
              </Text>
            </HStack>
          )}
        </Box>

        {/* Hidden container for Google Translate widget */}
        <Box 
          id="google_translate_element" 
          display="none"
          position="absolute"
          top="-9999px"
          left="-9999px"
          aria-hidden="true"
        />

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription fontSize="xs">
            <strong>Powered by Google Translate</strong><br />
            Translation quality may vary. For medical advice, please consult with our staff directly.
          </AlertDescription>
        </Alert>

        <Box bg="blue.50" p={3} borderRadius="md">
          <Text fontSize="xs" color="gray.600">
            <strong>Note:</strong> Translations are provided as a convenience. Lake Lucien Surgery Center is not responsible for translation accuracy. For important medical information, please contact us directly.
          </Text>
        </Box>

      </VStack>
    </Box>
  );
};

export default LanguageSelector;
